import { Box, debounce, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import DealerLocationMap from '../../components/DealerLocationMap/DealerLocationMap';
import flatten from 'flat';
import { toast } from 'react-toastify';
import Address from '../../@types/Address';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import Shape from '../../@types/Shape';
import { FormProvider, useForm } from 'react-hook-form';
import SplitColumnWrapper from '../../components/common/Form/SplitColumnWrapper/SplitColumnWrapper';
import TextField from '../../components/common/Form/FormFields/TextField/TextField';
import {
  loadCaptchaEnginge,
  LoadCanvasTemplate,
  validateCaptcha,
} from "react-simple-captcha";
import LoadingButton from '../../components/common/LoadingButton/LoadingButton';
import axios from '../../api/axios';
import CheckBox from '../../components/common/Form/FormFields/CheckBox/CheckBox';
import { SubHeading } from '../../components/common/PageHeadings/PageHeadings';
import { ProductInterest, DealerLocator } from '../../@types/DealerLocator';
import { CountryFieldAutoComplete } from '../../components/common/Form/FormFields/Autocomplete/Autocomplete';
import Countries from '../../data/Countries';
import useGoogleMapsLibrary from '../../hooks/useGoogleMapsLibrary';
import "yup-phone-lite";
import validateCountryPostalCode from '../../utils/validateZipCode';

interface DealerLocatorPageProps { }

const DealerLocatorPage: FC<DealerLocatorPageProps> = () => {

  const [formData, setFormData] = useState<DealerLocator | undefined>(undefined);

  const theme = useTheme();

  const isTablet = useMediaQuery(theme.breakpoints.down('tablet'));

  return (

    <Stack direction="column" sx={{ backgroundColor: 'white' }}>

      <Stack
        direction="column"
        sx={{
          backgroundColor: 'black',
          padding: '125px 15px 60px',
          color: 'white',
          backgroundImage: `url(https://madjax.com/wp-content/uploads/2024/05/mj-dash-bg-trans-20.svg)`
        }}>

        <Stack
          direction="column"
          spacing={2}
          sx={(theme) => ({
            width: '100%',
            maxWidth: '1440px',
            marginLeft: 'auto',
            marginRight: 'auto',
            [theme.breakpoints.down('tablet')]: {
              textAlign: 'center'
            }
          })}>

          <Typography variant='primaryFont' sx={{
            textTransform: 'uppercase',
            fontSize: '48px',
            fontWeight: 'bold',
            letterSpacing: '7.2px'
          }}>

            Find a MadJax Dealer

          </Typography>

          <Typography variant='primaryFont' sx={{}}>

            Our nationwide network of dealers proudly embodies MadJax’s top-tier standards by providing exceptional service and dedicated support.

          </Typography>
          <Typography variant='primaryFont'>

            Search for an authorized MadJax Vehicles dealer  or a friendly MadJax Accessories dealer near you.

          </Typography>

        </Stack>

      </Stack>

      <Stack
        sx={(theme) => ({
          width: '100%',
          maxWidth: '1440px',
          marginLeft: 'auto',
          marginRight: 'auto',
          padding: '10px 0 80px',
          boxSizing: 'border-box',
          [theme.breakpoints.down('laptopL')]: {
            paddingLeft: '2%',
            paddingRight: '2%',
          },
          [theme.breakpoints.down('tabletL')]: {
            paddingBottom: 0
          }
        })}>

        {!formData

          ? (<DealerLocatorForm setFormData={setFormData} />)

          : (<DealerLocationMap mapHeight={!isTablet ? 1200 : 700} zipCode={formData?.address?.zip} />)

        }

      </Stack>

    </Stack>

  );

}

interface DealerLocatorFormProps {

  setFormData: React.Dispatch<DealerLocator>

}

const DealerLocatorForm: FC<DealerLocatorFormProps> = ({ setFormData }) => {

  const [loading, setLoading] = useState<boolean>(false);

  const { geocodeAddress } = useGoogleMapsLibrary();

  // Handle captcha generation
  useEffect(() => {
    loadCaptchaEnginge(6);
  }, [])

  const onSubmit = async (data: DealerLocator) => {

    try {

      setLoading(true);

      setFormData(data);

      await axios.post('/lead', data);

    } catch (error) {

      console.log(error);

    } finally {

      setLoading(false);

    }

  }

  const onError = (errors: any) => {

    //axios.post('/email/form-error-log', { formName: 'DealerLocatorPage.tsx', formData: getValues(), formErrors: errors });

    const errorsFlattened: any = flatten(errors);

    const requiredErrors = Object.fromEntries(Object.entries(errorsFlattened).filter(([key, value]) => key.includes('.type')));

    if (Object.entries(requiredErrors).length <= 2) {

      for (const key in requiredErrors) {

        let keySplit = key.split('.');

        keySplit.pop();

        let formField = '';

        for (const fieldPart of keySplit) {

          formField += fieldPart.split(/([A-Z][a-z]+)/).map(s => s.charAt(0).toUpperCase() + s.slice(1)).join(' ');

        }

        toast.error(`Please check [${formField.trim()}] field for errors`);

      }

    } else {

      toast.error('Please check form for errors');

    }


  }

  const dealerLocatorFormSchema = yup.object<Shape<DealerLocator>>({

    firstName: yup.string().required('Please enter first name'),

    lastName: yup.string().required('Please enter last name'),

    phone: yup.string().phone().optional().when('address.countryCode', (value) => {
      return yup.string().phone(value, `Please enter validate phone number for country [${value}]`);
    }),

    email: yup.string()
      .strict()
      .trim('email address cannot include leading or trailing spaces')
      .email().email('Please enter a valid email address')
      .required(`Please enter an email address`),

    address: yup.object<Shape<Partial<Address>>>({

      zip: yup.string()
        .required('Please enter a zip or postal code')
        .when('countryCode', (value) => {
          return yup.string().required(`Invalid zip or postal code for country [${value}]`)
            .test('is-valid-zipcode', `Invalid zip or postal code for country [${value}]`, function () {
              const { zip, countryCode, lat, lng } = this.parent;
              return validateCountryPostalCode(countryCode, zip) && lat && lng
            })
        }),

      country: yup.string().required('Please enter a country'),

      countryCode: yup.string().required('Please enter a country code'),

      lat: yup.number().required('Please enter address lat'),

      lng: yup.number().required('Please enter address lng'),

      addressFormatted: yup.string().optional(),

      state: yup.string().optional(),

      stateCode: yup.string().optional(),

    }),

    productInterest: yup.object<Shape<ProductInterest>>({

      x4: yup.boolean().required('Please select true / X4'),

      x6: yup.boolean().required('Please select true / false X6'),

      e4: yup.boolean().required('Please select true / E4'),

      madjaxAccessories: yup.boolean().required('Please select true / false MadJax accessories'),

    }),

    optIn: yup.boolean().required(),

    captcha: yup.string().required('Please enter captcha').test('validated',
      `Please enter in a valid captcha`,
      (value) => validateCaptcha(value, false)
    ),

    leadType: yup.string().required('Please enter leadType')

  });

  const defaultValues: Partial<DealerLocator> = {

    optIn: true,

    leadType: 'Dealer Locator'

  }

  const methods = useForm<DealerLocator>({

    defaultValues: defaultValues,

    resolver: yupResolver(dealerLocatorFormSchema)

  });

  const { handleSubmit, getValues, setValue, watch, trigger, formState: { isSubmitted } } = methods;

  /*
    On load check if a country is populated into
    the form field on component mount If not we want to populate based
    on environment variable default country
  */
  useEffect(() => {

    if (!getValues('address.countryCode') && !getValues('address.country')) {

      Countries.forEach((country) => {

        if (Array.isArray(country.value)) {

          const countryFound = country.value.includes(process.env.REACT_APP_DEFAULT_COUNTRY ?? 'US');

          if (countryFound) {

            setValue('address.country', country.value[0]);

            setValue('address.countryCode', country.value[1]);

          }

        }

      });

    }

  }, [getValues, setValue]);

  const validateZipCode = useCallback(async () => {

    const zip = getValues('address.zip');

    const country = getValues('address.country');

    const results = await geocodeAddress('', '', '', zip ?? '', country ?? '');

    if (results) {

      const addressComponents = results?.address_components;

      const googleFoundState = addressComponents?.find((component) => component.types.includes('administrative_area_level_1'))

      setValue(`address.lat`, results.geometry.location.lat());

      setValue(`address.lng`, results.geometry.location.lng());

      setValue('address.addressFormatted', results.formatted_address);

      setValue('address.stateCode', googleFoundState?.short_name ?? undefined);

      setValue('address.state', googleFoundState?.long_name ?? undefined);

    } else {

      setValue(`address.lat`, undefined);

      setValue(`address.lng`, undefined);

      setValue('address.addressFormatted', undefined);

      setValue('address.stateCode', undefined);

      setValue('address.state', undefined);

    }

  }, [getValues, geocodeAddress, setValue, trigger]);

  const validateZipCodeDebounced = useMemo(() => debounce(validateZipCode, 500), [validateZipCode]);

  const zipCode = watch('address.zip');

  const countryCode = watch('address.countryCode');

  useEffect(() => {
    if (isSubmitted) {
      trigger('address.zip');
      trigger('phone');
    }
  }, [countryCode, isSubmitted, trigger])

  useEffect(() => {

    validateZipCodeDebounced();

  }, [zipCode, geocodeAddress, getValues, setValue, validateZipCodeDebounced])

  return (

    <Stack
      sx={{
        width: '100%',
        marginTop: '24px'
      }}>

      <FormProvider {...methods}>

        <form onSubmit={handleSubmit(onSubmit, onError)}>

          <Stack
            direction="column"
            spacing={4}>

            <Stack direction="column" spacing={2}>

              <SubHeading>

                Your Information

              </SubHeading>

              <SplitColumnWrapper>

                <TextField name="firstName" label="First Name" />

                <TextField name="lastName" label="Last Name" />

              </SplitColumnWrapper>

              <SplitColumnWrapper>

                <TextField name="phone" label="Phone Number (Optional)" />

                <TextField name="email" label="Email" />

              </SplitColumnWrapper>

              <SplitColumnWrapper>

                <TextField name="address.zip" label="Zip / Postal Code" />

                <CountryFieldAutoComplete
                  options={Countries}
                  name={'address.country'}
                  label={'Country'} />

              </SplitColumnWrapper>

            </Stack>

            <Stack direction="column" spacing={2}>

              <SubHeading>

                Products That Interest You

              </SubHeading>

              <Stack
                direction="row"
                useFlexGap
                flexWrap="wrap"
                spacing={2}
                sx={{ maxWidth: '900px', marginLeft: 'auto', marginRight: 'auto' }}>

                <CheckBox name="productInterest.x4" label="X4 (4 Passenger)" isTrueFalse={true} defaultValue={0} />

                <CheckBox name="productInterest.x6" label="X6 (6 Passenger)" isTrueFalse={true} defaultValue={0} />

                <CheckBox name="productInterest.e4" label="E4 (4 Passenger)" isTrueFalse={true} defaultValue={0} />

                <CheckBox name="productInterest.madjaxAccessories" label="MadJax Accessories" isTrueFalse={true} defaultValue={0} />

              </Stack>

              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{
                  paddingTop: '24px',
                  paddingBottom: '24px',
                  '& .MuiStack-root': { width: 'fit-content', flexGrow: 1 }
                }}>

                <Box maxWidth="180px">

                  <LoadCanvasTemplate width="fit-content" reloadText="Reload My Captcha" reloadColor="red" />

                </Box>

                <TextField name="captcha" label="Please Enter Captcha" />

              </Stack>

            </Stack>

            {/* <CheckBox
                      hidden={true}
                      name="optIn"
                      label="I would like to receive additional communications from MadJax"
                      isTrueFalse={true}
                      defaultValue={1} /> */}

            {/* <Typography
                      variant="secondaryFont"
                      fontSize="12px"
                      fontStyle="italic">
                      By opting into this form, you agree to receive communications, updates, and promotional material from us via email or other communication channels, and acknowledge that you can unsubscribe or opt-out at any time.
                      We will handle your personal information in accordance with our privacy policy.
                    </Typography> */}

          </Stack>

          <Stack
            direction="row"
            spacing={2}
            justifyContent="flex-end"
            sx={(theme) => ({
              width: '100%',
              marginRight: "12px",
              [theme.breakpoints.down('tablet')]: {
                justifyContent: "center",
                '& Button': {
                  width: '100%'
                }
              }
            })}>

            <LoadingButton
              loading={loading}
              type="submit">

              View Dealers

            </LoadingButton>

          </Stack>

        </form>

      </FormProvider>


    </Stack >

  )

}

export default DealerLocatorPage;
